import Vue from 'vue'
import _ from 'lodash'
import { applyPatch } from 'rfc6902'
import ComponentState, {
  ARTICLE_ID,
  ComponentStore,
  GerberaArticle,
  GerberaContent,
  GerberaHeader,
  IComponentData,
  IContainerData,
  isContainer,
  Patch
} from './state'

// Undo / Redo 履歴の最大件数
const MAX_HISTORY_LENGTH = 200

export function setComponent(state: ComponentState, components: IComponentData[] | IComponentData): void {
  const componentList = _.isArray(components) ? components : [components]
  batchUpdate(state, {
    update: componentList
  })
}

export function deleteComponent(state: ComponentState, components: IComponentData[] | IComponentData): void {
  const componentList = _.isArray(components) ? components : [components]
  batchUpdate(state, {
    remove: componentList
  })
}

export interface BatchUpdateRequest {
  update?: IComponentData[]
  remove?: IComponentData[]
}

export function batchUpdate(state: ComponentState, request: BatchUpdateRequest) {
  if (request.update) {
    request.update.forEach((c) => {
      // 変更前の値と c が同一のオブジェクトだった場合、コンポーネントが更新が通知されない。
      // 新しいオブジェクトに置き換えられたことを明示的に通知するため、常に clone を作成する。
      const clone = Object.assign({}, c)
      Vue.set(state.data, c.id, clone)
    })
  }
  if (request.remove) {
    request.remove.filter(isRemovable).forEach(c => Vue.delete(state.data, c.id))
  }
  cleanupState(state)
  markContentChanged(state)
}

export function setGerberaContent(state: ComponentState, content: GerberaContent): void {
  Vue.set(state, 'gerbera', content)
  markContentChanged(state)
}

export function setHeaderSectionContent(state: ComponentState, content: GerberaHeader): void {
  // フラット構造側のデータの更新を行う
  Vue.set(state.data.c__ARTICLE, 'header', content)
  // ツリー構造側のデータの更新を行う
  if (!state.gerbera.articleJson) {
    Vue.set(state.gerbera, 'articleJson', <GerberaArticle>{
      header: content
    })
    return
  }
  Vue.set(state.gerbera.articleJson, 'header', content)
  markContentChanged(state)
}

export function initializeContent(state: ComponentState, siteId: number): void {
  const initialState = <ComponentStore>{
    'c__ARTICLE': {
      type: 'article',
      id: 'c__ARTICLE',
      childIds: []
    }
  }
  Vue.set(state, 'data', initialState)
  Vue.set(state, 'gerbera', <GerberaContent>{
    siteId,
    category: {
      categoryId: null
    }
  })
  clearHistory(state)
  markContentSaved(state)
}

export function setAllComponents(state: ComponentState, components: ComponentStore): void {
  Vue.set(state, 'data', components)
  markContentChanged(state)
}

export function markContentSaved(state: ComponentState): void {
  state.isContentSaved = true
}

export function markContentChanged(state: ComponentState): void {
  state.isContentSaved = false
}

export function requestSaveState(state: ComponentState): void {
  state.timeStampForSaveState = new Date().getTime()
}

export function pushHistory(state: ComponentState, patch: Patch): void {
  const history = state.history

  // 不要になった Redo 履歴を削除する
  const patches = history.patches.slice(0, history.currentIndex)
  // 新しい履歴を追加する
  patches.push(patch)
  // 履歴の上限を越えたものを削除する
  patches.splice(0, Math.max((patches.length - MAX_HISTORY_LENGTH), 0))

  history.patches = patches
  history.currentIndex = history.patches.length
}

export function undo(state: ComponentState): void {
  const history = state.history
  const patchIndex = history.currentIndex - 1
  if (patchIndex < 0) {
    throw new TypeError('履歴がないので Undo できません。')
  }
  const patch = history.patches[patchIndex].undoPatch
  const stateClone = Object.assign({}, state)
  applyPatch(stateClone, patch)
  state.data = Object.assign({}, stateClone.data)
  state.gerbera = Object.assign({}, stateClone.gerbera)
  state.history.currentIndex = patchIndex
  markContentChanged(state)
}

export function redo(state: ComponentState): void {
  const history = state.history
  const patchIndex = history.currentIndex
  if (patchIndex >= history.patches.length) {
    throw new TypeError('履歴がないので Redo できません。')
  }
  const patch = history.patches[patchIndex].redoPatch
  const stateClone = Object.assign({}, state)
  applyPatch(stateClone, patch)
  state.data = Object.assign({}, stateClone.data)
  state.gerbera = Object.assign({}, stateClone.gerbera)
  state.history.currentIndex = patchIndex + 1
  markContentChanged(state)
}

export function clearHistory(state: ComponentState): void {
  state.history = {
    currentIndex: 0,
    patches: []
  }
}

export function applyBackup(state: ComponentState, backup: ComponentState): void {
  state.data = backup.data
  state.gerbera = backup.gerbera

  // Undo / Redo 履歴は復旧しない。コンポーネントのIDを再発行すると履歴が再現できないため
  clearHistory(state)
  // バックアップから復旧した場合、未保存とみなす
  markContentChanged(state)
  // バックアップから復旧する意義がないので、初期値にする
  state.timeStampForSaveState = 0
}

export default {
  setComponent,
  deleteComponent,
  batchUpdate,
  setGerberaContent,
  setHeaderSectionContent,
  initializeContent,
  setAllComponents,
  markContentSaved,
  markContentChanged,
  requestSaveState,
  pushHistory,
  undo,
  redo,
  clearHistory,
  applyBackup
}

function cleanupState(state: ComponentState): void {
  new EmptyContainerCleaner()
    .chain(new StripSingleChildContainerCleaner())
    .chain(new UnusedComponentCleaner())
    .clean(state)
}

function isRemovable(component: IComponentData): boolean {
  return component.type !== 'article'
}

/**
 * コンポーネントを整理するもの
 */
interface IComponentCleaner {
  clean: (state: ComponentState) => void
  chain: (cleander: IComponentCleaner) => IComponentCleaner
}

/**
 * IComponentCleaner 同士を連結するもの
 */
class ChainingComponentCleaner implements IComponentCleaner {
  private cleaners: IComponentCleaner[]

  constructor(...cleaners: IComponentCleaner[]) {
    this.cleaners = cleaners.splice(0) // clone Array
  }

  clean(state: ComponentState): void {
    this.cleaners.forEach(c => c.clean(state))
  }

  chain(cleaner: IComponentCleaner): IComponentCleaner {
    return new ChainingComponentCleaner(this, ...this.cleaners)
  }
}

abstract class BaseComponentCleaner implements IComponentCleaner {
  abstract clean(state: ComponentState): void

  chain(cleaner: IComponentCleaner): IComponentCleaner {
    return new ChainingComponentCleaner(this, cleaner)
  }
}

/**
 * 空のコンテナを削除するもの
 */
class EmptyContainerCleaner extends BaseComponentCleaner {
  private static DELETABLE_TYPES = ['section', 'rows', 'columns']

  clean(state: ComponentState): void {
    this.tryRemove(state)
  }

  /**
   * 空のコンテナを再帰的に探して削除する。
   * @param {ComponentState} state - 整理する state
   * @param {string} fromId - 起点となるコンポーネントのID
   * @returns {boolean} - コンポーネントが削除された場合、 true
   */
  private tryRemove(state: ComponentState, fromId: string = ARTICLE_ID): boolean {
    const component = state.data[fromId]
    if (!component) {
      return true
    }
    if (!isContainer(component)) {
      return false
    }

    const remainingIds = component.childIds.filter(id => !this.tryRemove(state, id))
    if (remainingIds.length === 0 && this.isDeletableContainer(component)) {
      Vue.delete(state.data, fromId)
      return true
    }
    if (remainingIds.length !== component.childIds.length) {
      Vue.set(state.data[fromId], 'childIds', remainingIds)
    }
    return false
  }

  private isDeletableContainer(container: IContainerData): boolean {
    return isRemovable(container) && EmptyContainerCleaner.DELETABLE_TYPES.indexOf(container.type) >= 0
  }
}

/**
 * 使われていないコンポーネントを削除するもの
 */
class UnusedComponentCleaner extends BaseComponentCleaner {
  clean(state: ComponentState): void {
    const whitelist = this.getWhitelist(state)
    _.map(state.data, (c, id) => id)
      .filter(id => whitelist.indexOf(id) < 0)
      .forEach(id => Vue.delete(state.data, id))
  }

  /**
   * 使われているコンポーネントのIDを取得する。
   * @param {ComponentState} state - 整理する state
   * @param {string} fromId - 起点となるコンポーネントのID
   * @returns {string[]} - 使われているID
   */
  private getWhitelist(state: ComponentState, fromId: string = ARTICLE_ID): string[] {
    const component = state.data[fromId]
    if (!component) {
      return []
    }
    if (!isContainer(component)) {
      return [fromId]
    }
    const whitelistFromChildren = component.childIds
      .map(id => this.getWhitelist(state, id))
      .reduce((ids1, ids2) => ids1.concat(ids2))
    return [fromId].concat(whitelistFromChildren)
  }
}

/**
 * 単一の子しか持たない、不要な中間コンテナを削除するもの
 */
class StripSingleChildContainerCleaner extends BaseComponentCleaner {
  private static DELETABLE_TYPES = ['rows', 'columns']

  clean(state: ComponentState): void {
    this.tryRemove(state)
  }

  /**
   * 単一の子しか持たないコンテナを再帰的に探して削除する。
   * @param {ComponentState} state - 整理する state
   * @param {string} fromId - 起点となるコンポーネントのID
   * @returns {boolean} - コンポーネントが削除された場合、 true
   */
  private tryRemove(state: ComponentState, fromId: string = ARTICLE_ID): void {
    const component = state.data[fromId]
    if (!component) {
      return
    }
    if (!isContainer(component)) {
      return
    }
    component.childIds.forEach(id => this.tryRemove(state, id))

    if (!this.isDeletableContainer(component)) {
      return
    }
    if (component.childIds.length === 1 && component.parentId) {
      const parent = <IContainerData>state.data[component.parentId]
      const child = state.data[component.childIds[0]]
      const index = parent.childIds.indexOf(fromId)
      if (index < 0) {
        throw new ReferenceError('コンポーネントツリーが不整合を起こしています。')
      }
      Vue.set(parent.childIds, index, child.id)
      Vue.set(child, 'parentId', parent.id)
      Vue.delete(state.data, fromId)
    }
  }

  private isDeletableContainer(container: IContainerData): boolean {
    return isRemovable(container) && StripSingleChildContainerCleaner.DELETABLE_TYPES.indexOf(container.type) >= 0
  }
}
